import type { Palette } from "@mui/material";
import {
	Building2,
	Car,
	Gem,
	Handshake,
	House,
	type LucideIcon,
	Palette as PaletteIcon,
	ReceiptText,
} from "lucide-react";
import {
	ClientPortfolioDomainEnum,
	ClientPropertyTypeEnum,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	type useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { PortfolioCategoryIds } from "web_myplann/Portfolio/Types/portfolioCategory";

type TranslateFunction = ReturnType<typeof useResource>["t"];

export type PortfolioItemAppearance = {
	bg: string;
	color: string;
	abbreviation?: string;
	Icon?: LucideIcon;
};

type IconDomain =
	| ClientPortfolioDomainEnum.Services
	| ClientPortfolioDomainEnum.PaidAdvisory;

const DOMAIN_ICON: Record<IconDomain, LucideIcon> = {
	[ClientPortfolioDomainEnum.Services]: ReceiptText,
	[ClientPortfolioDomainEnum.PaidAdvisory]: Handshake,
};

const isIconDomain = (
	domain: ClientPortfolioDomainEnum,
): domain is IconDomain => domain in DOMAIN_ICON;

const PROPERTY_TYPE_ICON: Record<ClientPropertyTypeEnum, LucideIcon> = {
	[ClientPropertyTypeEnum.AutoMoto]: Car,
	[ClientPropertyTypeEnum.Art]: PaletteIcon,
	[ClientPropertyTypeEnum.Commodity]: Gem,
	[ClientPropertyTypeEnum.Flat]: House,
	[ClientPropertyTypeEnum.House]: House,
	[ClientPropertyTypeEnum.Cottage]: House,
	[ClientPropertyTypeEnum.Land]: Building2,
};

type Source = {
	domain?: ClientPortfolioDomainEnum | null;
	propertyType?: ClientPropertyTypeEnum | null;
};

export const getPortfolioItemAppearance = (
	palette: Palette,
	t: TranslateFunction,
	{ domain, propertyType }: Source,
): PortfolioItemAppearance => {
	if (domain && !propertyType) {
		const { tint, ink } = palette.portfolioArea[domain];
		return isIconDomain(domain)
			? { bg: tint, color: ink, Icon: DOMAIN_ICON[domain] }
			: {
					bg: tint,
					color: ink,
					abbreviation: t(
						Resources.Portfolio.List.PortfolioDomainAbbreviation[domain],
					),
				};
	}

	const { tint, ink } = palette.portfolioArea[PortfolioCategoryIds.property];
	return {
		bg: tint,
		color: ink,
		Icon: propertyType ? PROPERTY_TYPE_ICON[propertyType] : House,
	};
};
